import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, CheckCircle2, Info, Loader2, X, XCircle } from 'lucide-react';

/* ═══════════════════════════════════════════════════════════════════════
   2. RETOURS — dire ce qui vient de se passer

   - Toast : confirmation DISCRÈTE d'une action finie, ou échec ponctuel.
     Succès et information disparaissent seuls ; une erreur reste jusqu'à
     ce qu'on la ferme. Un toast peut porter une action (« Annuler »).
   - Encart (`.encart`) : message posé près de son origine, qui reste.
   - Bandeau : information qui concerne toute la page.
   - Confirmation : avant une action sensible — remplace `window.confirm`,
     qui ne dit pas ce qu'on va perdre et ne s'habille pas.

   `annoncer()` et `confirmer()` s'appellent de n'importe où, y compris
   hors d'un composant (couche API) : ils passent par des événements.
   ═══════════════════════════════════════════════════════════════════════ */

export const EVT_TOAST = 'assopro:toast';
export const EVT_CONFIRMATION = 'assopro:confirmation';

/** Afficher un toast depuis n'importe où.
 *  ton : 'succes' | 'erreur' | 'attention' | 'info' */
export function annoncer(message, { ton = 'info', titre, action, duree } = {}) {
  window.dispatchEvent(new CustomEvent(EVT_TOAST, { detail: { message, ton, titre, action, duree } }));
}
export const annoncerSucces = (message, options) => annoncer(message, { ...options, ton: 'succes' });
export const annoncerErreur = (message, options) => annoncer(message, { ...options, ton: 'erreur' });
export const annoncerAttention = (message, options) => annoncer(message, { ...options, ton: 'attention' });

/* « Supprimer ce participant ? Son compte sera désactivé. » devient :
   titre « Supprimer ce participant ? », explication « Son compte sera
   désactivé. », bouton « Supprimer » — le verbe de la question, plutôt
   qu'un « OK » qui ne dit pas ce qu'il fait. */
const VERBES = ['Supprimer', 'Retirer', 'Annuler', 'Lancer', 'Clôturer', 'Valider', 'Envoyer', 'Désactiver', 'Réinitialiser', 'Rejeter', 'Refuser', 'Publier', 'Archiver', 'Effacer', 'Signer'];
function depuisQuestion(texte) {
  const i = texte.indexOf('?');
  const titre = i > 0 ? texte.slice(0, i + 1).trim() : texte.trim();
  const message = i > 0 ? texte.slice(i + 1).trim() : '';
  const verbe = VERBES.find((v) => titre.startsWith(v));
  const signer = /^Confirmez-vous la signature/.test(titre);
  return { titre, message, confirmer: signer ? 'Signer' : verbe || 'Confirmer' };
}

/** Demander un texte (un motif, le plus souvent) dans la même fenêtre :
 *  remplace `window.prompt`. Résout la saisie, ou `null` si l'on annule.
 *  { titre, message, libelle: 'Motif', placeholder, obligatoire: true, confirmer: 'Refuser' } */
export function demanderMotif(options) {
  const { libelle = 'Motif', placeholder = '', obligatoire = true, ...reste } = options;
  return confirmer({ ...reste, saisie: { libelle, placeholder, obligatoire } });
}

/** Demander une confirmation ; résout `true` si la personne confirme.
 *  { titre, message, confirmer: 'Supprimer', annuler: 'Annuler', danger: true } */
export function confirmer(options) {
  const opts = typeof options === 'string' ? depuisQuestion(options) : options;
  return new Promise((resolve) => {
    const recu = { ...opts, resolve };
    const evenement = new CustomEvent(EVT_CONFIRMATION, { detail: recu, cancelable: true });
    // Sans fournisseur monté (test, page isolée), on retombe sur le natif
    // plutôt que de laisser la promesse pendre pour toujours.
    if (window.dispatchEvent(evenement)) {
      resolve(opts.saisie ? window.prompt(opts.titre || opts.message) : window.confirm(opts.message || opts.titre));
    }
  });
}

const ICONES = { succes: CheckCircle2, erreur: XCircle, attention: AlertTriangle, info: Info };
const DUREES = { succes: 4000, info: 5000, attention: 7000, erreur: null };

function Toast({ toast, onFermer }) {
  const Icone = ICONES[toast.ton] || Info;
  useEffect(() => {
    const duree = toast.duree !== undefined ? toast.duree : (toast.action ? 8000 : DUREES[toast.ton]);
    if (!duree) return undefined;
    const t = setTimeout(() => onFermer(toast.id), duree);
    return () => clearTimeout(t);
  }, [toast, onFermer]);

  return (
    <div className="toast" data-ton={toast.ton} role={toast.ton === 'erreur' ? 'alert' : 'status'}>
      <Icone className="toast-icone w-[18px] h-[18px]" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        {toast.titre && <p className="font-semibold m-0">{toast.titre}</p>}
        <p className="m-0 text-[var(--text-main,#33465C)]">{toast.message}</p>
        {toast.action && (
          <button type="button" className="mt-1.5 text-[13px] font-semibold underline underline-offset-2"
                  onClick={() => { toast.action.onClick?.(); onFermer(toast.id); }}>
            {toast.action.libelle}
          </button>
        )}
      </div>
      <button type="button" onClick={() => onFermer(toast.id)} aria-label="Fermer"
              className="shrink-0 -m-1 p-1 rounded-md text-[var(--text-muted)] hover:text-[var(--text-bold)]">
        <X className="w-4 h-4" aria-hidden="true" />
      </button>
    </div>
  );
}

function PileToasts() {
  const [toasts, setToasts] = useState([]);
  const compteur = useRef(0);
  const fermer = useCallback((id) => setToasts((l) => l.filter((t) => t.id !== id)), []);

  useEffect(() => {
    const recevoir = (e) => {
      const d = e.detail || {};
      if (!d.message) return;
      setToasts((l) => {
        // Le même message deux fois de suite (dix requêtes en échec) n'en
        // fait qu'un : une pile de doublons noie l'information.
        if (l.some((t) => t.message === d.message && t.ton === d.ton)) return l;
        compteur.current += 1;
        return [...l.slice(-3), { ...d, id: compteur.current }];
      });
    };
    window.addEventListener(EVT_TOAST, recevoir);
    return () => window.removeEventListener(EVT_TOAST, recevoir);
  }, []);

  return createPortal(
    <div className="pile-toasts" aria-live="polite">
      {toasts.map((t) => <Toast key={t.id} toast={t} onFermer={fermer} />)}
    </div>,
    document.body,
  );
}

function FenetreConfirmation() {
  const [demande, setDemande] = useState(null);
  const [enCours, setEnCours] = useState(false);
  const [texte, setTexte] = useState('');
  const boutonAnnuler = useRef(null);
  const champ = useRef(null);

  useEffect(() => {
    const recevoir = (e) => { e.preventDefault(); setDemande(e.detail); setEnCours(false); setTexte(''); };
    window.addEventListener(EVT_CONFIRMATION, recevoir);
    return () => window.removeEventListener(EVT_CONFIRMATION, recevoir);
  }, []);

  // Une demande de saisie résout le texte (ou null) ; une confirmation, un booléen.
  const repondre = useCallback((oui, saisi) => {
    setDemande((d) => {
      if (d) d.resolve(d.saisie ? (oui ? saisi : null) : oui);
      return null;
    });
  }, []);

  useEffect(() => {
    if (!demande) return undefined;
    // Le focus va sur « Annuler » : une validation par réflexe (Entrée)
    // ne doit pas déclencher l'action sensible.
    if (demande.saisie) champ.current?.focus(); else boutonAnnuler.current?.focus();
    const touche = (e) => { if (e.key === 'Escape') repondre(false); };
    document.addEventListener('keydown', touche);
    return () => document.removeEventListener('keydown', touche);
  }, [demande, repondre]);

  if (!demande) return null;
  const saisieManquante = !!demande.saisie?.obligatoire && !texte.trim();
  const danger = demande.danger !== false && /supprim|retir|annul|désactiv|refus|rejet|effac/i.test(`${demande.confirmer || ''} ${demande.titre || ''} ${demande.message || ''}`)
    ? true : !!demande.danger;

  return createPortal(
    <div className="fixed inset-0 z-[250] grid place-items-center p-4 bg-black/50 backdrop-blur-[2px]"
         onMouseDown={(e) => { if (e.target === e.currentTarget) repondre(false); }}>
      <div role="alertdialog" aria-modal="true" aria-labelledby="confirmation-titre" aria-describedby="confirmation-texte"
           className="w-full max-w-md rounded-[var(--r-carte,22px)] bg-[var(--bg-card,#fff)] p-6 shadow-2xl"
           style={{ animation: 'etats-entre .18s cubic-bezier(.22,1,.36,1)' }}>
        <div className="flex items-start gap-3">
          <span className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${danger ? 'bg-[var(--alerte-fond)] text-[var(--alerte)]' : 'bg-[var(--info-fond)] text-[var(--text-bold)]'}`}>
            {danger ? <AlertTriangle className="w-5 h-5" /> : <Info className="w-5 h-5" />}
          </span>
          <div className="min-w-0">
            <h2 id="confirmation-titre" className="text-[16px] font-semibold text-[var(--text-bold)] m-0">
              {demande.titre || 'Confirmer cette action ?'}
            </h2>
            {demande.message && (
              <p id="confirmation-texte" className="text-[13.5px] text-[var(--text-main)] mt-1.5 mb-0 leading-relaxed whitespace-pre-line">{demande.message}</p>
            )}
          </div>
        </div>
        {demande.saisie && (
          <label className="block mt-4">
            <span className="block text-[12px] font-semibold text-[var(--text-muted)] mb-1.5">
              {demande.saisie.libelle}{demande.saisie.obligatoire ? ' *' : ''}
            </span>
            <textarea ref={champ} rows={3} value={texte} onChange={(e) => setTexte(e.target.value)}
                      placeholder={demande.saisie.placeholder} maxLength={2000}
                      className="champ w-full text-[13.5px] resize-y" />
          </label>
        )}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 mt-6">
          <button ref={boutonAnnuler} type="button" onClick={() => repondre(false)} className="act act-contour">
            {demande.annuler || 'Annuler'}
          </button>
          <button type="button" disabled={enCours || saisieManquante}
                  onClick={() => { setEnCours(true); repondre(true, texte.trim()); }}
                  className={`act ${danger ? 'act-danger-plein' : ''}`}>
            {enCours && <Loader2 className="w-4 h-4 animate-spin" />}
            {demande.confirmer || 'Confirmer'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** Monté une fois, à la racine : la pile de toasts et la fenêtre de confirmation. */
export function FournisseurRetours({ children }) {
  return (
    <>
      {children}
      <PileToasts />
      <FenetreConfirmation />
    </>
  );
}

/* Le contexte reste disponible pour qui préfère un hook. */
const Contexte = createContext({ annoncer, confirmer });
export const useRetours = () => useContext(Contexte);

/** Une information qui concerne toute la page, qu'on peut fermer ou non. */
export function Bandeau({ ton = 'info', titre, children, action, onFermer }) {
  const classe = { info: 'encart-info', succes: 'encart-ok', attention: 'encart-attention', erreur: 'encart-alerte' }[ton] || 'encart-info';
  const Icone = ICONES[ton] || Info;
  return (
    <div className={`encart ${classe} flex items-start gap-3`} role={ton === 'erreur' ? 'alert' : 'status'}>
      <Icone className="w-[18px] h-[18px] shrink-0 mt-[1px]" aria-hidden="true" />
      <div className="flex-1 min-w-0">
        {titre && <p className="font-semibold m-0">{titre}</p>}
        <div className="m-0 text-[13.5px]">{children}</div>
      </div>
      {action}
      {onFermer && (
        <button type="button" onClick={onFermer} aria-label="Fermer" className="shrink-0 opacity-70 hover:opacity-100">
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
